import { DeviceService } from '@peoplepower/webcore/dist/types/data/services/deviceService';
import { inject, injectable } from '../../modules/common/di';
import { DevicesAdminApi, SimCardStatus } from '../api/admin/devices/devicesApi';
import { GetDevicesApiResponse } from '../api/admin/devices/getDevicesApiResponse';
import { SimulatedDeviceCopyApiResponse } from "../api/admin/devices/simulatedDeviceCopyApiResponse";
import { ApiResponseBase } from '../models/apiResponseBase';

@injectable('DeviceAdminService')
export class DeviceAdminService extends DeviceService {
  @inject('DevicesAdminApi') private readonly devicesAdminApi!: DevicesAdminApi;

  constructor() {
    super();
  }

  /**
   * Gets devices according to the specified params.
   *
   * @param {number} [organizationId] Optional Organization ID.
   * @param [params] Request parameters.
   * @param {number} [params.userId] Filter by user ID.
   * @param {number} [params.locationId] Filter by location ID.
   * @param {string} [params.deviceId] Filter by device ID.
   * @param {boolean} [params.tree] Retrieve devices from sub-locations as well.
   * @param {number|number[]} [params.deviceType] Filter by device types. Multiple values supported.
   * @param {string} [params.searchBy] Search by device ID or description. Use * for a wildcard.
   * @param {string} [params.searchTag] Search by tag.
   * @param {string} [params.lessUpdateDate] Request devices, where the last update date is less than this.
   * @param {string} [params.moreUpdateDate] Request devices, where the last update date is more than this.
   * @param {string} [params.paramName] Request devices, which sent this parameter to the cloud.
   * @param {string} [params.paramValue] Requested parameter value.
   * @param {number} [params.limit] Limit the response size by this number.
   * @param {boolean} [params.getTags] Return device tags.
   * @param {string} [params.sortCollection] Collection name to sort (should be "devices" here).
   * @returns {Promise<DevicesList>}
   */
  public searchDevices(
    organizationId?: number,
    params?: {
      userId?: number;
      locationId?: number;
      deviceId?: string;
      tree?: boolean;
      deviceType?: number | number[];
      searchBy?: string;
      searchTag?: string;
      lessUpdateDate?: string;
      moreUpdateDate?: string;
      paramName?: string;
      paramValue?: string;
      limit?: number;
      getTags?: boolean;
      sortCollection?: string;
    },
  ): Promise<DevicesList> {
    const parameters = {
      ...params,
      sortCollection: 'devices',
    };

    return this.authService.ensureAuthenticated()
      .then(() => this.devicesAdminApi.getDevices(organizationId, parameters));
  }

  /**
   * Simulated Device Copy.
   * Create a device simulator - an exact copy of a real device that would receive all the same data as a real device.
   * Using this API an administrator can set a device as a source simulator and register another simulated device on a required location.
   *
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/cloud.html#tag/Devices/operation/Create%20Device%20Simulated%20Copy}
   *
   * @param {number} [locationId] Location ID that contains the source device.
   * @param {string} [deviceId] Device ID acting as a source.
   * @param [params] Request parameters.
   * @param {number} [params.simulatedLocationId] Location ID where a simulated devise will be registered. Optional.
   * @returns {Promise<SimulatedDeviceCopyApiResponse>}
   */
  public simulatedDeviceCopy(
    locationId: number,
    deviceId: string,
    params?: {
      simulatedLocationId?: number;
    },
  ): Promise<SimulatedDeviceCopyApiResponse> {
    return this.authService.ensureAuthenticated()
      .then(() => this.devicesAdminApi.simulatedDeviceCopy(locationId, deviceId, params));
  }

  /**
   * Activate Sim Card.
   * Using this API an administrator can manually activate or deactivate device SIM cards.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/cloud.html#tag/Devices/operation/Activate%20Sim%20Card}
   *
   * @param {number} locationId Location ID with registered device.
   * @param {string} deviceId Target device ID with SIM card(s).
   * @param params Request parameters.
   * @param {SimCardStatus} [params.status] New SIM card status.
   * @param {number} [params.simId] SIM card ID.
   * @returns {Promise<ApiResponseBase>}
   */
  public activateSimCard(
    locationId: number,
    deviceId: string,
    params: {
      status: SimCardStatus,
      simId?: number,
    },
  ): Promise<ApiResponseBase> {
    return this.authService.ensureAuthenticated()
      .then(() => this.devicesAdminApi.activateSimCard(locationId, deviceId, params));
  }
}

export interface DevicesList extends GetDevicesApiResponse {
}
